/**
 * @type Feature (Lint Stabilization)
 * @rationale Replace 'any' in fetchInterceptor.ts to satisfy Pass 3 of the stabilization plan.
 */
import { errorForwardingService } from './errorForwarding';
import { sanitizeUrl } from './sanitize';

// Store original fetch
const originalFetch = window.fetch;

async function parseResponseData(response: Response): Promise<unknown> {
  try {
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('application/json')) {
      const cloned = response.clone();
      return await cloned.json();
    } else if (contentType?.includes('text/')) {
      const cloned = response.clone();
      const text = await cloned.text();
      return text.substring(0, 200); // First 200 chars
    }

    return `[${contentType || 'unknown type'}]`;
  } catch {
    return '[Unable to read response]';
  }
}

export function setupFetchInterceptor() {
  // Override global fetch
  window.fetch = async function (...args: Parameters<typeof fetch>): Promise<Response> {
    const [resource, options] = args;
    const url = typeof resource === 'string' ? resource : (resource instanceof Request ? resource.url : resource.toString());
    const method = options?.method?.toUpperCase() || 'GET';

    // F3: the forwarder POSTs to /api/console-error via this same overridden
    // window.fetch, so a non-ok telemetry response would re-report itself. Skip
    // the self-report (but still return/re-throw as usual). Segment-bounded so
    // the /api/console-errors/* admin routes (recent/clear) are NOT skipped —
    // a plain substring match would swallow their failures too.
    const isTelemetry = /\/api\/console-error(\?|$)/.test(url);

    // F4: mirror the axios /auth skip. Auth/token requests carry code /
    // client_assertion in the body and tokens in the response; never forward
    // either to telemetry. Report status/message/url only.
    const isAuth =
      url.includes('/auth/') ||
      /login\.microsoftonline\.com|login\.microsoft\.com|login\.windows\.net|sts\.windows\.net/i.test(url);

    // Match isTelemetry/isAuth on the raw url (path is preserved), but forward
    // a query-redacted URL so `?access_token=…`/`?code=…` never reach telemetry
    // via the requestUrl field or the message string.
    const safeUrl = sanitizeUrl(url) ?? url;

    try {
      // Make the actual request
      const response = await originalFetch(...args);

      // Check if response is an error status
      if (!response.ok && !isTelemetry) {
        const responseData = await parseResponseData(response);
        // Backend X-Request-ID for log correlation, same as the axios
        // interceptor. Headers.get is case-insensitive per the Fetch spec.
        const requestId = response.headers.get('x-request-id') ?? undefined;

        // Report HTTP error. For auth/token URLs, omit both request and response
        // bodies (they carry credentials/tokens); still report status/message/url.
        errorForwardingService.reportNetworkError({
          message: `Fetch Error: HTTP ${response.status} ${response.statusText} - ${method} ${safeUrl}`,
          httpMethod: method,
          requestUrl: safeUrl,
          statusCode: response.status,
          // Redaction is centralized in errorForwardingService (choke point).
          requestData: isAuth ? undefined : options?.body,
          responseData: isAuth ? undefined : responseData,
          errorCode: `HTTP_${response.status}`,
          requestId,
        });
      }

      return response;
    } catch (error: unknown) {
      // Network error, CORS error, or other fetch failure
      let errorMessage = 'Fetch failed';
      let errorCode = 'FETCH_ERROR';

      if (error instanceof Error) {
        errorMessage = error.message;
        if (error.name === 'AbortError') {
            errorMessage = `Fetch Aborted: ${method} ${safeUrl}`;
            errorCode = 'FETCH_ABORTED';
        } else if (error instanceof TypeError) {
            if (errorMessage.includes('Failed to fetch')) {
                errorMessage = `Network Error: Failed to fetch ${safeUrl}. Could be CORS, server down, or connection issue.`;
                errorCode = 'FETCH_NETWORK_ERROR';
            } else if (errorMessage.includes('NetworkError')) {
                errorMessage = `Network Error: ${method} ${safeUrl} - ${errorMessage}`;
                errorCode = 'FETCH_NETWORK_ERROR';
            }
        }
      }

      // Report error (skip self-reporting telemetry failures; omit auth bodies).
      const stack = error instanceof Error ? error.stack : undefined;
      if (!isTelemetry) {
        errorForwardingService.reportNetworkError({
          message: errorMessage,
          httpMethod: method,
          requestUrl: safeUrl,
          // Redaction is centralized in errorForwardingService (choke point).
          requestData: isAuth ? undefined : options?.body,
          errorCode,
          stack,
        });
      }

      // Re-throw so app can handle it
      throw error;
    }
  };
}

// Restore original fetch (for cleanup if needed)
export function restoreFetch() {
  window.fetch = originalFetch;
}
